import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import StoreIcon from "@mui/icons-material/Store";
import PersonIcon from "@mui/icons-material/Person";
import HikingIcon from "@mui/icons-material/Hiking";
import EngineeringIcon from "@mui/icons-material/Engineering";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import {
  Alert,
  Avatar,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useGetContextsQuery } from "Auth/Api/Queries/Context/useGetContextsQuery";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { useSwitchContextWithReload } from "Hooks/useSwitchContextWithReload";
import type { ContextDto } from "Infrastructure/Api/Api";
import { type AccessRoleCode, isBackOfficeRole } from "Models/AccessRoleCodes";
import { Resources, useResource } from "Translations/Resources";
import { getContextProfilePhotoUrl } from "Utils/Image/getProfilePhotoUrl";
import { appUrl } from "Utils/UrlUtils";
import { useNavigate } from "Navigation/useNavigate";
import type { ReactNode } from "react";
import { getContextDisplay } from "Components/Layout/Menu/ContextSwitcher/utils";

type Props = {
  onContextSwitched?: () => void;
};

const ROLE_ICON_MAP: Record<string, ReactNode> = {
  Customer: <PersonIcon fontSize="small" />,
  Hunter: <HikingIcon fontSize="small" />,
  HunterPlus: <HikingIcon fontSize="small" />,
  Merchant: <StoreIcon fontSize="small" />,
  Employee: <EngineeringIcon fontSize="small" />,
  Admin: <AdminPanelSettingsIcon fontSize="small" />,
  Aml: <AdminPanelSettingsIcon fontSize="small" />,
  CustomerOnboarding: <AdminPanelSettingsIcon fontSize="small" />,
  AdminLite: <AdminPanelSettingsIcon fontSize="small" />,
  Disponent: <StoreIcon fontSize="small" />,
  Child: <PersonIcon fontSize="small" />,
} satisfies Record<AccessRoleCode, ReactNode>;

const COMPONENT_RESOURCES = Resources.Auth.ContextSwitcher;

export const ContextSwitcher = ({ onContextSwitched }: Props) => {
  const { t } = useResource();
  const user = useLoggedInUser();
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetContextsQuery(!!user);

  const { switchContext, isSwitching } = useSwitchContextWithReload({
    onBeforeReload: onContextSwitched,
  });

  if (isLoading) {
    return (
      <Stack alignItems="center" py={2}>
        <CircularProgress size={20} />
      </Stack>
    );
  }

  const contexts = data?.contexts;

  // Contexts only exist for Identity-model accounts — legacy sessions get neither the switcher
  // nor the add-role entry. A single-context (Customer-only) account still sees "Add role".
  if (error || !contexts || contexts.length === 0) {
    return null;
  }

  // A back-office account is its own Identity with exactly one context and never gains another
  // role, so it gets no "Add role" entry either. The button sits outside the multi-context guard
  // below on purpose (a Customer-only account still sees it), hence the separate check here.
  const isBackOfficeAccount = contexts.some((context) =>
    isBackOfficeRole(context.accessRoleCode),
  );

  // With a single context there are no rows to switch between, and a back-office account has no
  // "Add role" either — rendering the section would leave a "Switch role" heading over nothing.
  if (isBackOfficeAccount && contexts.length <= 1) {
    return null;
  }

  const handleAddRole = () => {
    onContextSwitched?.();
    navigate(appUrl("add-role"));
  };

  const isActiveContext = ({ identityUserPublicID }: ContextDto) =>
    identityUserPublicID === data?.activeIdentityUserPublicID;

  const handleSwitch = (context: ContextDto) => {
    if (isActiveContext(context) || isSwitching) {
      return;
    }
    switchContext({ identityUserPublicID: context.identityUserPublicID });
  };

  return (
    <Stack gap={1}>
      <Typography variant="caption" color="text.secondary" px={1}>
        {t(COMPONENT_RESOURCES.Title)}
      </Typography>
      <List dense disablePadding>
        {contexts.length > 1 &&
          contexts.map((context) => {
            const { identityUserPublicID, accessRoleCode } = context;
            const { primary, secondary } = getContextDisplay(context, t);
            const isActive = isActiveContext(context);
            const roleIcon = ROLE_ICON_MAP[accessRoleCode] ?? (
              <PersonIcon fontSize="small" />
            );
            // undefined when the persona has no picture — the raw stored value is the
            // cache-buster, so photo-less contexts keep the plain role icon (no request).
            const photoUrl = getContextProfilePhotoUrl(
              identityUserPublicID,
              context.profilePicture,
            );

            return (
              <ListItemButton
                key={identityUserPublicID}
                onClick={() => handleSwitch(context)}
                disabled={isSwitching || isActive}
                selected={isActive}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {photoUrl ? (
                    // The role icon doubles as the fallback while loading or when the
                    // photo answers 204 (e.g. an SSO-URL value the context route won't serve).
                    <Avatar src={photoUrl} sx={{ width: 28, height: 28 }}>
                      {roleIcon}
                    </Avatar>
                  ) : (
                    roleIcon
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={primary}
                  secondary={secondary}
                  slotProps={{
                    primary: { variant: "body2" },
                    secondary: { variant: "caption" },
                  }}
                />
                {isActive && <CheckIcon fontSize="small" color="primary" />}
              </ListItemButton>
            );
          })}
        {!isBackOfficeAccount && (
          <ListItemButton onClick={handleAddRole} disabled={isSwitching}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t(COMPONENT_RESOURCES.AddRole)}
              slotProps={{ primary: { variant: "body2" } }}
            />
          </ListItemButton>
        )}
      </List>
      {isSwitching && (
        <Alert severity="info" variant="outlined">
          {t(COMPONENT_RESOURCES.Switching)}
        </Alert>
      )}
    </Stack>
  );
};
